// ---------------------------------------------------------------------------
// C51: Progressive Profiling — pre-fill known, hide completed, show new
// ---------------------------------------------------------------------------
import type { FormField, CrmFieldMapping } from "@/hooks/use-forms";

// CRM fields that map to form field types
const CRM_FIELD_MAP: Record<string, string> = {
  firstName: "text",
  lastName: "text",
  email: "email",
  phone: "phone",
  company: "text",
};

export interface ContactProfile {
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  company?: string;
  [key: string]: string | undefined;
}

/**
 * Given a form's fields and CRM mappings, determine which fields
 * should be shown, hidden, or pre-filled for a known contact.
 */
export function applyProgressiveProfiling(
  fields: FormField[],
  mappings: CrmFieldMapping[],
  contactProfile: ContactProfile | null,
): Array<FormField & { prefilled?: string; hidden?: boolean }> {
  if (!contactProfile) {
    // Unknown visitor — show all fields
    return fields.map((f) => ({ ...f, prefilled: undefined, hidden: false }));
  }

  return fields.map((field) => {
    // Find if this form field maps to a CRM field
    const mapping = mappings.find((m) => m.formFieldId === field.id);
    if (!mapping) {
      // No CRM mapping — show as normal
      return { ...field, prefilled: undefined, hidden: false };
    }

    const crmValue = contactProfile[mapping.crmField];
    if (crmValue && crmValue.trim()) {
      // CRM already has this data — hide the field (progressive profiling)
      return { ...field, prefilled: crmValue, hidden: true };
    }

    // CRM doesn't have this data — show the field
    return { ...field, prefilled: undefined, hidden: false };
  });
}

/**
 * Count how many fields are hidden vs shown for a contact.
 * Used to show "We already know 3 of your details" message.
 */
export function profilingStats(
  fields: Array<{ hidden?: boolean }>,
): { total: number; shown: number; hidden: number } {
  const inputFields = fields.filter((f) => !(f as FormField).type || !["heading", "paragraph", "divider"].includes((f as FormField).type));
  const hidden = inputFields.filter((f) => f.hidden).length;
  return {
    total: inputFields.length,
    shown: inputFields.length - hidden,
    hidden,
  };
}
